import React, { useEffect, useRef, useState } from "react";
import "./DOTTrendGraph.css";

function DOTTrendGraph({
  title,
  tags,
  values,
  onClose
}) {

  const canvasRef = useRef(null);

  const [history, setHistory] = useState({});
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState(0);
  const [hover, setHover] = useState(null);

  // 🔥 DRAG WINDOW
  const [position, setPosition] = useState({ x: 250, y: 120 });
  const draggingWindow = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // 🔥 RESIZE
  const [size, setSize] = useState({ w: 650, h: 320 });
  const resizing = useRef(false);

  // 🔥 PAN GRAPH
  const panning = useRef(false);
  const panStartX = useRef(0);

  const maxPoints = 300;

  // ================= HISTORY =================
  useEffect(() => {

    const interval = setInterval(() => {

      setHistory(prev => {
        const updated = { ...prev };

        tags.forEach(tag => {

          if (!updated[tag.key]) updated[tag.key] = [];

          updated[tag.key].push(Number(values[tag.key] || 0));

          if (updated[tag.key].length > maxPoints) {
            updated[tag.key].shift();
          }
        });

        return updated;
      });

    }, 400);

    return () => clearInterval(interval);

  }, [values, tags]);

  // ================= DRAW =================
  useEffect(() => {

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = size.w;
    canvas.height = size.h;

    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    // ================= GLOBAL SCALE =================
    let allValues = [];

    tags.forEach(tag => {
      allValues = allValues.concat(history[tag.key] || []);
    });

    if (allValues.length === 0) return;

    let min = Math.min(...allValues);
    let max = Math.max(...allValues);

    // 🔥 padding (fix your "top line issue")
    const padding = (max - min) * 0.2 || 1;
    min -= padding;
    max += padding;

    const range = max - min || 1;

    // ================= GRID =================
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    for (let i = 0; i <= 5; i++) {
      const y = (i / 5) * height;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // ================= DRAW LINES =================
    tags.forEach(tag => {

      const series = history[tag.key] || [];
      if (series.length < 2) return;

      const visible = Math.floor(maxPoints / zoom);
      const start = Math.max(0, series.length - visible - offset);
      const sliced = series.slice(start, start + visible);

      ctx.beginPath();
      ctx.strokeStyle = tag.color;
      ctx.lineWidth = 2;

      sliced.forEach((val, i) => {

        const x = (i / visible) * width;

        const normalized = (val - min) / range;
        const y = height - normalized * height;

        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });

      ctx.stroke();
    });

    // ================= CURSOR =================
    if (hover) {
      ctx.strokeStyle = "#ffffff";
      ctx.beginPath();
      ctx.moveTo(hover.x, 0);
      ctx.lineTo(hover.x, height);
      ctx.stroke();
    }

  }, [history, zoom, offset, size, hover, tags]);

  // ================= ZOOM =================
  const handleWheel = (e) => {
    e.preventDefault();
    setZoom(z => Math.max(1, Math.min(10, z + (e.deltaY > 0 ? -0.5 : 0.5))));
  };

  // ================= PAN =================
  const handleMouseDown = (e) => {
    panning.current = true;
    panStartX.current = e.clientX;
  };

  const handleMouseUp = () => {
    panning.current = false;
  };

  const handleMouseMove = (e) => {

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;

    setHover({ x });

    if (!panning.current) return;

    const dx = e.clientX - panStartX.current;

    setOffset(prev => Math.max(0, prev - Math.floor(dx / 5)));

    panStartX.current = e.clientX;
  };

  // ================= DRAG WINDOW =================
  const startDrag = (e) => {
    draggingWindow.current = true;
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  useEffect(() => {

    const move = (e) => {

      if (draggingWindow.current) {
        setPosition({
          x: e.clientX - dragOffset.current.x,
          y: e.clientY - dragOffset.current.y
        });
      }

      if (resizing.current) {
        setSize({
          w: Math.max(400, e.clientX - position.x),
          h: Math.max(250, e.clientY - position.y)
        });
      }
    };

    const up = () => {
      draggingWindow.current = false;
      resizing.current = false;
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };

  }, [position]);

  // ================= LEGEND =================
  const renderLegend = () => (
    <div className="dot-legend">
      {tags.map(tag => (
        <div key={tag.key} className="dot-legend-item">
          <span style={{ background: tag.color }} />
          {tag.label}
        </div>
      ))}
    </div>
  );

  return (
    <div
      className="dot-trend-container"
      style={{ left: position.x, top: position.y }}
    >

      <div className="dot-header" onMouseDown={startDrag}>
        <span>{title}</span>
        <button onClick={onClose}>✕</button>
      </div>

      {renderLegend()}

      <canvas
        ref={canvasRef}
        className="dot-canvas"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      />

      {/* 🔥 RESIZE HANDLE */}
      <div
        className="dot-resize"
        onMouseDown={() => (resizing.current = true)}
      />

    </div>
  );
}

export default DOTTrendGraph;